import { gql } from '@apollo/client';
import { endOfToday, sub } from 'date-fns';
import { useMemo } from 'react';

import { Server } from '../../apollo/types';
import { useAppDependenciesContext } from '../../state';
import {
  IntersessionSummaryConnected__GetUpcomingAppointmentForPatientQuery,
  IntersessionSummaryConnected__GetUpcomingAppointmentForPatientQueryVariables,
} from './types';

const TIME_START_LOWER_BOUND_MINUTES = 15;

/**
 * Query to fetch upcoming appointment with all related data including patient info and pre-visit summary.
 */
export const INTERSESSION_SUMMARY_QUERY = gql`
  query IntersessionSummary($filters: AppointmentsFilter!) {
    allAppointments(filters: $filters) {
      edges {
        node {
          id
          appointmentShortId
          appointmentType
          timeStart
          timeEnd
          inOffice
          patient {
            id
            shortId
            firstName
            preferredFullName
            pronouns
            clientUser {
              shortId
              clientUserShortId
            }
          }
          recurringAppointmentGrouping {
            indefinite
            recurringAppointmentGroupingId
            weeksBetween
            recurrenceRule
            version
            appointments {
              id
              timeStart
            }
          }
          preVisitSummary {
            createdAt
            content {
              __typename
              ... on IntakePreVisitSummaryContentObject {
                currentPhqCategory
                currentGadCategory
                generatedCoachTopics
              }
              ... on FollowUpPreVisitSummaryContentObject {
                progressNote {
                  label
                  item
                }
                currentPhqCategory
                phqDifference
                currentGadCategory
                gadDifference
                reflectionTopics
                completedResources
                generatedCoachTopics
              }
            }
          }
        }
      }
    }
  }
`;

function getFifteenMinutesAgoISO() {
  return sub(new Date(), {
    minutes: TIME_START_LOWER_BOUND_MINUTES,
  }).toISOString();
}

/**
 * Hook that fetches the upcoming appointment with all related data including
 * patient information and pre-visit summary.
 */
export function useIntersessionSummary() {
  const { useQuery } = useAppDependenciesContext();

  const fifteenMinutesAgoISO = useMemo(getFifteenMinutesAgoISO, []);

  const { data, loading } = useQuery<
    IntersessionSummaryConnected__GetUpcomingAppointmentForPatientQuery,
    IntersessionSummaryConnected__GetUpcomingAppointmentForPatientQueryVariables
  >(INTERSESSION_SUMMARY_QUERY, {
    variables: {
      filters: {
        timeStartRange: {
          begin: fifteenMinutesAgoISO,
          end: endOfToday().toISOString(),
        },
        canceled: false,
      },
    },
    context: {
      server: Server.MONOLITH,
    },
  });

  const appointment = data?.allAppointments?.edges?.[0]?.node;

  return {
    appointment,
    loading,
  };
}
